import React, { useState, useEffect, useCallback } from 'react'
import { Button, Checkbox, Col, DatePicker, Divider, Form, Image, Input, Row, Select, Space, Switch, Typography } from 'antd'
import { EditOutlined, CheckOutlined, InboxOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { useAuthContext } from 'contexts/Auth'
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';
import JoditEditor from 'jodit-react';
import dayjs from 'dayjs';
import axios from 'axios';

import genders from 'data/genders.json'
import learningTypes from 'data/learningTypes.json'
import languages from 'data/languages.json'
import courseTypes from 'data/courseTypes.json'
import currencies from 'data/currencies.json'
import paymentTypes from 'data/paymentTypes.json'
import statuses from 'data/statuses.json'

const { Title, Text } = Typography
const { Option } = Select

const initialState = {
  isAdmissionOpen: false, lastDateOfAdmission: null, imageURL: "", bannerURL: "",
  title: "", slug: "", abbreviation: "", summary: "", batch: "", learningTypes: [], languages: [],
  currency: "PKR", fee: null, paymentType: null, discount: 0, netFee: null, scholarshipType: null, scholarship: "",
  type: null, targetGenders: [],
  instructorForMales: { name: "", profileURL: "", photoURL: "", bio: "" }, instructorForFemales: { name: "", profileURL: "", photoURL: "", bio: "" }, isInstructorSameForBoth: false, isCourseDurationSameForBoth: false,
  courseDurationForMales: "", classDurationForMales: "", classDaysForMales: "", classSessionsForMales: [{ type: null, time: null }],
  courseDurationForFemales: "", classDurationForFemales: "", classDaysForFemales: "", classSessionsForFemales: [{ type: null, time: null }],
  sortOrder: null, status: null,
  description: "", courseContent: "", additionalText: "",
}

export default function Add() {

  const { getApiConfig } = useAuthContext()
  const [isNew, setIsNew] = useState(true)
  const [state, setState] = useState(initialState)
  const [image, setImage] = useState(null)
  const [isImageVisible, setIsImageVisible] = useState(false)
  const [banner, setBanner] = useState(null)
  const [isBannerVisible, setIsBannerVisible] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSlugEditable, setIsSlugEditable] = useState(false)
  const [isAbbreviationEditable, setIsAbbreviationEditable] = useState(false)

  const params = useParams()

  // Dropzone
  const { getRootProps: getBannerRootProps, getInputProps: getBannerInputProps } = useDropzone({
    accept: { 'image/*': [] }, multiple: false,
    onDrop: acceptedFiles => { setBanner(acceptedFiles[0]) }
  });
  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'image/*': [] }, multiple: false,
    onDrop: acceptedFiles => { setImage(acceptedFiles[0]) }
  });

  const readCourse = useCallback(async () => {
    if (params.id !== "add") {
      axios.get(`${window.api}/courses/single/${params.id}`, getApiConfig())
        .then((res) => {
          const { data, status } = res
          if (status === 200) {
            const { course } = data
            if (course.scholarshipType === "") { course.scholarshipType = null }
            setState(s => ({ ...s, ...course }))
            setIsNew(false)
          }
        })
        .catch(error => {
          console.error(error)
          window.toastify(error.response?.data?.message || "Something went wrong while getting the course", "error")
        })
    } else {
      setState(initialState)
      setIsNew(true)
    }
  }, [params.id, getApiConfig])
  useEffect(() => { readCourse() }, [readCourse])

  const handleChange = e => setState(s => ({ ...s, [e.target.name]: e.target.value }))
  const handleChangeTitle = e => {
    const { value } = e.target
    const title = value
    const slug = window.getSlug(value)
    const abbreviation = window.getAbbreviation(value)
    setState(s => ({ ...s, title, slug, abbreviation }))
  }
  const handleChangeSelect = (name, value) => setState(s => ({ ...s, [name]: value }))
  const handleChangeDiscount = e => {
    const { value } = e.target
    if (!value || value < 0 || value > 100) {
      return window.toastify("Discount should be between 0 and 100", "info")
    }
    setState(s => ({ ...s, discount: value }))
  }
  useEffect(() => {
    const fee = state.fee
    const discount = state.discount
    if (fee && (discount || discount === 0)) {
      let netFee = fee - (fee * discount / 100)
      netFee = netFee.toFixed(2)
      netFee = Number(netFee)
      setState(s => ({ ...s, netFee }))
    }
  }, [state.fee, state.discount])

  const handleChangeInstructor = (e, instructor) => setState(s => ({ ...s, [instructor]: { ...s[instructor], [e.target.name]: e.target.value } }))
  const handleChangeSession = (sessions, index, name, value) => {
    const newSessions = state[sessions].map((session, i) => i === index ? { ...session, [name]: value } : session)
    setState(s => ({ ...s, [sessions]: newSessions }))
  }

  const handleAddSession = (sessions) => {
    const newSessions = [...state[sessions], { type: null, time: null }]
    setState(s => ({ ...s, [sessions]: newSessions }))
  }
  const handleDeleteSession = (sessions, index) => {
    const newSessions = state[sessions].filter((session, i) => i !== index)
    setState(s => ({ ...s, [sessions]: newSessions }))
  }

  const isBothGenderSelected = state.targetGenders.includes("male") && state.targetGenders.includes("female")

  const handleSubmit = e => {
    e.preventDefault()

    let {
      isAdmissionOpen, lastDateOfAdmission, imageURL, bannerURL, title, slug, abbreviation, summary, batch, learningTypes, languages,
      currency, fee, paymentType, discount, netFee, scholarshipType, scholarship, type, targetGenders, instructorForMales, instructorForFemales, isInstructorSameForBoth, isCourseDurationSameForBoth,
      courseDurationForMales, classDurationForMales, classDaysForMales, classSessionsForMales, courseDurationForFemales, classDurationForFemales, classDaysForFemales, classSessionsForFemales,
      sortOrder, status, description, courseContent, additionalText,
    } = state

    title = title.trim()
    slug = slug.trim()
    abbreviation = abbreviation.trim()
    summary = summary.trim()

    if (!title) { return window.toastify('Please enter title', "error") }
    if (!batch) { return window.toastify('Please enter batch', "error") }
    if (batch < 9 && batch.charAt(0) !== "0") { batch = "0" + batch }
    if (!slug) { return window.toastify('Please enter slug', "error") }
    if (!abbreviation) { return window.toastify('Please enter abbreviation', "error") }
    if (!summary) { return window.toastify('Please enter summary', "error") }
    if (!type) { return window.toastify('Please select course type', "error") }
    if (!learningTypes.length) { return window.toastify('Please select learning types', "error") }
    if (!languages.length) { return window.toastify('Please select languages', "error") }
    if (!targetGenders.length) { return window.toastify('Please select target gender(s)', "error") }
    if (targetGenders.includes("male")) {
      if (!instructorForMales.name) { return window.toastify('Please enter instructor for males name', "error") }
      if (!instructorForMales.bio) { return window.toastify('Please enter instructor for males bio', "error") }
      if (!courseDurationForMales) { return window.toastify('Please enter course duration for males', "error") }
      if (!classDurationForMales) { return window.toastify('Please enter class duration', "error") }
      if (!classDaysForMales) { return window.toastify('Please enter class days for', "error") }
      if (!classSessionsForMales.length) { return window.toastify('Please add atleast one class session for males', "error") }
      for (const session of classSessionsForMales) {
        if (!session.type) { return window.toastify('Please select class session type for males', "error") }
        if (!session.time) { return window.toastify('Please enter class session time for males', "error") }
      }
    }
    if (targetGenders.includes("females")) {
      if (!instructorForFemales.name) { return window.toastify('Please enter instructor for females name', "error") }
      if (!instructorForFemales.bio) { return window.toastify('Please enter instructor for females bio', "error") }
      if (!courseDurationForFemales) { return window.toastify('Please enter course duration for females', "error") }
      if (!classDurationForFemales) { return window.toastify('Please enter class duration', "error") }
      if (!classDaysForFemales) { return window.toastify('Please enter class days for', "error") }
      if (!classSessionsForFemales.length) { return window.toastify('Please add atleast one class session for females', "error") }
      for (const session of classSessionsForFemales) {
        if (!session.type) { return window.toastify('Please select class session type for females', "error") }
        if (!session.time) { return window.toastify('Please enter class session time for females', "error") }
      }
    }
    if (!currency) { return window.toastify('Please select currency', "error") }
    if (!fee) { return window.toastify('Please enter fee', "error") }
    if (!paymentType) { return window.toastify('Please select payment type', "error") }
    if (!discount && discount !== 0) { return window.toastify('Please enter discount', "error") }
    if (!netFee) { return window.toastify('Please enter net fee', "error") }
    if (!scholarshipType) { scholarshipType = "" }
    if (isAdmissionOpen && !lastDateOfAdmission) { return window.toastify('Please select last date of admission', "error") }
    if (!sortOrder) { return window.toastify('Please enter sort order', "error") }
    if (!status) { return window.toastify('Please select status', "error") }

    if (!banner && !bannerURL) { return window.toastify("Please upload banner", "error") }
    if (banner && banner.size > 1048576) { return window.toastify("Banner file size should be less than 1MB", "error") }
    if (!image && !imageURL) { return window.toastify("Please upload course poster", "error") }
    if (image && image.size > 1048576) { return window.toastify("Poster file size should be less than 1MB", "error") }

    if (!isBothGenderSelected) {
      isInstructorSameForBoth = false
      isCourseDurationSameForBoth = false
    }

    learningTypes = JSON.stringify(learningTypes)
    languages = JSON.stringify(languages)
    targetGenders = JSON.stringify(targetGenders)
    instructorForMales = JSON.stringify(instructorForMales)
    instructorForFemales = JSON.stringify(instructorForFemales)
    classSessionsForMales = JSON.stringify(classSessionsForMales)
    classSessionsForFemales = JSON.stringify(classSessionsForFemales)

    let data = {
      isAdmissionOpen, lastDateOfAdmission, imageURL, bannerURL,
      title, slug, abbreviation, summary, batch, learningTypes, languages,
      currency, fee, paymentType, discount, netFee, scholarshipType, scholarship, type, targetGenders,
      instructorForMales, instructorForFemales, isInstructorSameForBoth, isCourseDurationSameForBoth,
      courseDurationForMales, classDurationForMales, classDaysForMales, classSessionsForMales,
      courseDurationForFemales, classDurationForFemales, classDaysForFemales, classSessionsForFemales,
      sortOrder, status, description, courseContent, additionalText,
    }

    const formData = new FormData()
    for (const key in data) { formData.append(key, data[key]); }
    if (image) { formData.append('image', image) }
    if (banner) { formData.append('banner', banner) }

    setIsProcessing(true)
    createDocument(formData)
  }

  const createDocument = async (formData) => {

    axios[isNew ? "post" : "patch"](`${window.api}/courses/${isNew ? "create" : `update/${state.id}`}`, formData, getApiConfig())
      .then((res) => {
        const { data, status } = res
        if (status === isNew ? 201 : 200) {
          window.toastify(data.message, "success")
        }
      })
      .catch(error => {
        console.error(error)
        window.toastify(error.response?.data?.message || `Something went wrong while ${isNew ? "adding a new" : "updating the"} course`, "error")
      })
      .finally(() => {
        setIsProcessing(false)
      })

  }

  return (
    <>
      <Row className='mb-4'>
        <Col span={24}>
          <div className="flex-between">
            <Title level={3} className="mb-0">Courses</Title>
            <Button type='primary' loading={isProcessing} onClick={handleSubmit}>{isNew ? "Add" : "Update"}</Button>
          </div>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col xs={24} lg={18}>
          <div className="card">
            <Row className='mb-4'>
              <Col>
                <Title level={4} className="mb-0">{isNew ? "Add New" : "Update"} Course</Title>
              </Col>
            </Row>
            <Form layout='vertical'>
              <Row gutter={16}>
                <Col xs={24} lg={16}>
                  <Form.Item label="Title" required>
                    <Input placeholder="Enter title" name="title" value={state.title} onChange={handleChangeTitle} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Batch" required>
                    <Input placeholder="Enter batch" name="batch" value={state.batch} onChange={handleChange} onKeyDown={window.onlyNumber} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={16}>
                  <Form.Item label="Slug" required>
                    <Space.Compact className='w-100'>
                      <Input placeholder="Enter slug" name="slug" value={state.slug} disabled={!isSlugEditable} onChange={handleChange} />
                      <Button onClick={() => { setIsSlugEditable(isEditable => !isEditable) }} icon={!isSlugEditable ? <EditOutlined /> : <CheckOutlined />} />
                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={8}>
                  <Form.Item label="Abbreviation" required>
                    <Space.Compact className='w-100'>
                      <Input placeholder="Enter abbreviation for registration number" name="abbreviation" value={state.abbreviation} disabled={!isAbbreviationEditable} onChange={handleChange} />
                      <Button onClick={() => { setIsAbbreviationEditable(isEditable => !isEditable) }} icon={!isAbbreviationEditable ? <EditOutlined /> : <CheckOutlined />} />
                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label="Summary" required>
                    <Input placeholder="Enter summary" name="summary" value={state.summary} onChange={handleChange} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Course Type" required>
                    <Select showSearch placeholder="Select course type" value={state.type} onChange={value => handleChangeSelect("type", value)} options={courseTypes} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Learning Types" required>
                    <Select mode='tags' showSearch placeholder="Select learning types (you can choose one or more)" value={state.learningTypes} onChange={value => handleChangeSelect("learningTypes", value)} options={learningTypes} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Languages" required>
                    <Select mode='tags' showSearch placeholder="Select languages (you can choose one or more)" value={state.languages} onChange={value => handleChangeSelect("languages", value)} options={languages} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Target Gender(s)" required>
                    <Select mode='tags' showSearch placeholder="Select gender(s) you want to target for the course" value={state.targetGenders} onChange={value => handleChangeSelect("targetGenders", value)} options={genders} />
                  </Form.Item>
                </Col>
                {isBothGenderSelected && <Col xs={24} className='mb-4'>
                  <Checkbox checked={state.isInstructorSameForBoth} onChange={e => setState(s => ({ ...s, isInstructorSameForBoth: e.target.checked }))}>Instructor Same for Both</Checkbox>
                  <Checkbox checked={state.isCourseDurationSameForBoth} onChange={e => setState(s => ({ ...s, isCourseDurationSameForBoth: e.target.checked }))}>Course Duration, Class Duration, Days, and Sessions Same for Both</Checkbox>
                </Col>
                }

                {state.targetGenders.includes("male") &&
                  <>
                    <Col span={24}>
                      <Divider className='mt-0'>Details for Males</Divider>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Full Name" required>
                        <Input placeholder="Enter full name of instructor" name="name" value={state.instructorForMales?.name} onChange={e => handleChangeInstructor(e, "instructorForMales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Profile URL">
                        <Input placeholder="Enter profile URL of instructor" name="profileURL" value={state.instructorForMales?.profileURL} onChange={e => handleChangeInstructor(e, "instructorForMales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Photo URL">
                        <Input placeholder="Enter photo URL of instructor" name="photoURL" value={state.instructorForMales?.photoURL} onChange={e => handleChangeInstructor(e, "instructorForMales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Bio" required>
                        <Input placeholder="Enter bio of instructor" name="bio" value={state.instructorForMales?.bio} onChange={e => handleChangeInstructor(e, "instructorForMales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Course Duration" required>
                        <Input placeholder="Enter course duration i.e. 6 Months" name="courseDurationForMales" value={state.courseDurationForMales} onChange={handleChange} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Class Duration" required>
                        <Input placeholder="Enter class duration i.e. 2 Hours" name="classDurationForMales" value={state.classDurationForMales} onChange={handleChange} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Class Days" required>
                        <Input placeholder="Enter class days i.e. 2 Days A Week (Monday - Tuesday)" name="classDaysForMales" value={state.classDaysForMales} onChange={handleChange} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={24}>
                      <Form.Item label="Class Sessions" required>
                        <Space direction='vertical' className='w-100'>
                          {state.classSessionsForMales.map((session, i) => (
                            <Space.Compact key={i} className='w-100'>
                              <Select showSearch placeholder="Select session type" style={{ maxWidth: 160 }} value={session.type} onChange={value => handleChangeSession("classSessionsForMales", i, "type", value)}>
                                {learningTypes.map((item, i) => <Option key={i} value={item.value}>{item.shortLabel}</Option>)}
                              </Select>
                              <Input placeholder="Enter session time i.e. 10:00 AM - 12:00 PM" name="time" value={session.time} onChange={e => handleChangeSession("classSessionsForMales", i, "time", e.target.value)} />
                              <Button type='primary' danger onClick={() => handleDeleteSession("classSessionsForMales", i)}>Delete</Button>
                            </Space.Compact>
                          ))}
                        </Space>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={{ span: 12, offset: 6 }} className='mb-4'>
                      <Button type='primary' block className='flex-center' icon={<PlusSquareOutlined />} onClick={() => handleAddSession("classSessionsForMales")}>Add Session</Button>
                    </Col>
                  </>
                }
                {state.targetGenders.includes("female") &&
                  <>
                    <Col span={24}>
                      <Divider className='mt-0'>Details for Females</Divider>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Full Name" required>
                        <Input placeholder="Enter full name of instructor" name="name" value={state.instructorForFemales?.name} onChange={e => handleChangeInstructor(e, "instructorForFemales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Profile URL">
                        <Input placeholder="Enter profile URL of instructor" name="profileURL" value={state.instructorForFemales?.profileURL} onChange={e => handleChangeInstructor(e, "instructorForFemales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Photo URL">
                        <Input placeholder="Enter photo URL of instructor" name="photoURL" value={state.instructorForFemales?.photoURL} onChange={e => handleChangeInstructor(e, "instructorForFemales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24}>
                      <Form.Item label="Bio" required>
                        <Input placeholder="Enter bio of instructor" name="bio" value={state.instructorForFemales?.bio} onChange={e => handleChangeInstructor(e, "instructorForFemales")} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Course Duration" required>
                        <Input placeholder="Enter course duration i.e. 6 Months" name="courseDurationForFemales" value={state.courseDurationForFemales} onChange={handleChange} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Class Duration" required>
                        <Input placeholder="Enter class duration i.e. 2 Hours" name="classDurationForFemales" value={state.classDurationForFemales} onChange={handleChange} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Form.Item label="Class Days" required>
                        <Input placeholder="Enter class days i.e. 2 Days A Week (Monday - Tuesday)" name="classDaysForFemales" value={state.classDaysForFemales} onChange={handleChange} />
                      </Form.Item>
                    </Col>
                    <Col xs={24} lg={24}>
                      <Form.Item label="Class Sessions" required>
                        <Space direction='vertical' className='w-100'>
                          {state.classSessionsForFemales.map((session, i) => (
                            <Space.Compact key={i} className='w-100'>
                              <Select showSearch placeholder="Select session type" style={{ maxWidth: 160 }} value={session.type} onChange={value => handleChangeSession("classSessionsForFemales", i, "type", value)}>
                                {learningTypes.map((item, i) => <Option key={i} value={item.value}>{item.shortLabel}</Option>)}
                              </Select>
                              <Input placeholder="Enter session time i.e. 10:00 AM - 12:00 PM" name="time" value={session.time} onChange={e => handleChangeSession("classSessionsForFemales", i, "time", e.target.value)} />
                              <Button type='primary' danger onClick={() => handleDeleteSession("classSessionsForFemales", i)}>Delete</Button>
                            </Space.Compact>
                          ))}
                        </Space>
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={{ span: 12, offset: 6 }} className='mb-4'>
                      <Button type='primary' block className='flex-center' icon={<PlusSquareOutlined />} onClick={() => handleAddSession("classSessionsForFemales")}>Add Session</Button>
                    </Col>
                  </>
                }

                <Col xs={24} xxl={12}>
                  <Form.Item label="Fee" required>
                    <Space.Compact className='w-100'>

                      <Select showSearch placeholder="Select currency" style={{ maxWidth: 110 }} value={state.currency} onChange={value => handleChangeSelect("currency", value)}>
                        {currencies.map((currency, i) => <Option key={i} value={currency.value}>{currency.value} ({currency.symbol})</Option>)}
                      </Select>

                      <Input placeholder="Enter fee (0 will consider free)" name="fee" value={state.fee} onChange={handleChange} onKeyDown={window.onlyNumber} />

                      <Select showSearch placeholder="Select payment type" style={{ maxWidth: 170 }} value={state.paymentType} onChange={value => handleChangeSelect("paymentType", value)}>
                        {paymentTypes.map((paymentType, i) => <Option key={i} value={paymentType.value}>{paymentType.label}</Option>)}
                      </Select>

                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={7} xxl={3}>
                  <Form.Item label="Discount (%)">
                    <Input placeholder="Enter discount in percentage" name="discount" value={state.discount} onChange={handleChangeDiscount} onKeyDown={window.onlyNumber} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={7} xxl={3}>
                  <Form.Item label="Net Fee">
                    <Input placeholder="Net fee" name="netFee" value={state.netFee} readOnly />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={10} xxl={6}>
                  <Form.Item label="Scholarship (%)">
                    <Space.Compact className='w-100'>

                      <Select showSearch placeholder="Select type" style={{ maxWidth: 110 }} value={state.scholarshipType} onChange={value => handleChangeSelect("scholarshipType", value)}>
                        {[{ label: "Flat", value: "flat" }, { label: "Upto", value: "upto" }].map((item, i) => <Option key={i} value={item.value}>{item.label}</Option>)}
                      </Select>

                      <Input placeholder="Enter scholarship in percentage" name="scholarship" value={state.scholarship} onChange={handleChange} onKeyDown={window.onlyNumber} />

                    </Space.Compact>
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Sort Order" required>
                    <Input placeholder="Enter sort order" name="sortOrder" value={state.sortOrder} onChange={handleChange} onKeyDown={window.onlyNumber} />
                  </Form.Item>
                </Col>
                <Col xs={24} lg={12}>
                  <Form.Item label="Status" required>
                    <Select showSearch placeholder="Select status" value={state.status} onChange={value => handleChangeSelect("status", value)} options={statuses} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
          <div className="card mt-3">
            <Row>
              <Col span={24}>
                <Title level={4}>Description</Title>
                <JoditEditor value={state.description} onChange={value => handleChangeSelect("description", value)} />
              </Col>
            </Row>
          </div>
          <div className="card mt-3">
            <Row>
              <Col span={24}>
                <Title level={4}>Course Content</Title>
                <JoditEditor value={state.courseContent} onChange={value => handleChangeSelect("courseContent", value)} />
              </Col>
            </Row>
          </div>
          <div className="card mt-3">
            <Row>
              <Col span={24}>
                <Title level={4}>Additional Text</Title>
                <JoditEditor value={state.additionalText} onChange={value => handleChangeSelect("additionalText", value)} />
              </Col>
            </Row>
          </div>
        </Col>
        <Col xs={24} lg={6} className='mt-3 mt-lg-0'>
          <div className="card p-2">
            <div className="flex-between mb-2">
              <Text className='fw-bold'>Admissions</Text>
              <Switch checkedChildren="Open" unCheckedChildren="Closed" value={state.isAdmissionOpen} onChange={val => handleChangeSelect("isAdmissionOpen", val)} />
            </div>
            <Text className='mb-1'>Last date of admission</Text>
            <DatePicker className='w-100' value={state.lastDateOfAdmission ? dayjs(state.lastDateOfAdmission) : null} onChange={(dateObj, dateStr) => setState(s => ({ ...s, lastDateOfAdmission: dateStr }))}
              disabledDate={(current) => window.disabledDate(current, "past")} disabled={!state.isAdmissionOpen}
            />
          </div>
          <div className="card p-2 my-3">
            <Text className='mb-1'><Text style={{ color: "#ff4d4f" }}>*</Text> <b>Banner</b> (1920 x 400) px</Text>
            <div {...getBannerRootProps({ className: 'dropzone text-center' })}>
              <InboxOutlined />
              <input {...getBannerInputProps()} />
              <p>Click or drag image</p>
            </div>
            {(banner || state.bannerURL) && <Text className='text-primary cursor-pointer text-decoration-underline' onClick={() => { setIsBannerVisible(true) }}>Click to view banner</Text>}
          </div>
          <div className="card p-2">
            <Text className='mb-1'><Text style={{ color: "#ff4d4f" }}>*</Text> <b>Poster</b> (1000 x 1000) px</Text>
            <div {...getRootProps({ className: 'dropzone text-center' })}>
              <InboxOutlined />
              <input {...getInputProps()} />
              <p>Click or drag image</p>
            </div>
            {(image || state.imageURL) && <Text className='text-primary cursor-pointer text-decoration-underline' onClick={() => { setIsImageVisible(true) }}>Click to view image</Text>}
          </div>
        </Col>
      </Row>

      {(image || state.imageURL) && <Image preview={{ visible: isImageVisible, src: image ? URL.createObjectURL(image) : state.imageURL, onVisibleChange: (value) => { setIsImageVisible(value) } }} />}
      {(banner || state.bannerURL) && <Image preview={{ visible: isBannerVisible, src: banner ? URL.createObjectURL(banner) : state.bannerURL, onVisibleChange: (value) => { setIsBannerVisible(value) } }} />}
    </>
  )
}
